import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function GRNDialog({ open, onOpenChange, po, onSuccess }) {
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  
  React.useEffect(function() {
    if (po) {
      const gItems = po.items.map(function(item) {
        return {
          product_id: item.product_id,
          product_name: item.product_name,
          ordered_quantity: item.quantity,
          received_quantity: item.quantity,
        };
      });
      setItems(gItems);
      setNotes('');
    }
  }, [po]);
  
  function updateQty(index, qty) {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], received_quantity: qty };
    setItems(newItems);
  }
  
  async function handleSubmit() {
    if (!po) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: 'Bearer ' + token };
      
      const payload = {
        purchase_order_id: po.id,
        received_date: new Date().toISOString().split('T')[0],
        items: items.map(function(item) {
          return {
            product_id: item.product_id,
            ordered_quantity: item.ordered_quantity,
            received_quantity: Number(item.received_quantity) || 0,
          };
        }),
        notes: notes,
      };
      
      await axios.post(API + '/grn', payload, { headers });
      toast.success('Stock received successfully');
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to receive stock');
    } finally {
      setLoading(false);
    }
  }
  
  if (!po) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Receive Stock - {po.po_number}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          <div className="bg-blue-50 p-3 rounded-lg text-sm">
            <p><strong>Supplier:</strong> {po.supplier_name}</p>
            {po.retailer_name && (
              <p><strong>For Retailer:</strong> {po.retailer_name}</p>
            )}
          </div>
          
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Product</th>
                  <th className="px-4 py-2 text-center">Ordered</th>
                  <th className="px-4 py-2 text-center">Received</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map(function(item, idx) {
                  return (
                    <tr key={item.product_id || ('grn-' + idx)} data-testid={'grn-item-' + idx}>
                      <td className="px-4 py-2">{item.product_name}</td>
                      <td className="px-4 py-2 text-center">{item.ordered_quantity}</td>
                      <td className="px-4 py-2">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.received_quantity}
                          onChange={function(e) { updateQty(idx, e.target.value); }}
                          className="h-8 text-center w-24 mx-auto"
                          data-testid={'grn-qty-' + idx}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          <div className="space-y-2">
            <Label>Notes (Optional)</Label>
            <Input
              value={notes}
              onChange={function(e) { setNotes(e.target.value); }}
              placeholder="Any remarks about this receipt..."
              data-testid="grn-notes"
            />
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={function() { onOpenChange(false); }} data-testid="grn-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={loading} data-testid="grn-submit-btn">
              {loading ? 'Processing...' : 'Receive Stock'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default GRNDialog;
